import SearchOutlined from '@ant-design/icons/lib/icons/SearchOutlined';
import { IdType } from '@cbinsights/cbientityservice/cbientityservice';
import { Form, Space, Spin } from 'antd';
import { NamePath } from 'antd/lib/form/interface';
import Select, { DefaultOptionType } from 'antd/lib/select';
import { useSearchForEntity } from 'client/modules/cbi-entity/services/general-info/hooks/UseSearchForEntity';
import { debounce } from 'lodash';
import { ReactElement, useCallback, useState } from 'react';

export type EntitySearchProps = {
  placeholder?: string;
  name: NamePath;
  label: string;
};

export const EntitySearchSelectFormItem = ({
  placeholder = 'Search for an entity...',
  name,
  label,
}: EntitySearchProps): ReactElement => {
  const [search, setSearch] = useState<string>('');
  const entity = useSearchForEntity(
    {
      searchTerm: search,
      entityType: IdType.CBI_ENTITY,
      searchVcFunds: true,
      limit: 200,
      searchDrafts: true,
    },
    !!search
  );

  const debounceFetcher = useCallback(
    debounce((searchTerm) => {
      setSearch(searchTerm);
    }, 300),
    []
  );

  const entities: DefaultOptionType[] = (
    entity?.data?.entitySearchResults ?? []
  ).map((el) => ({
    value: JSON.stringify(el),
    label: (
      <div className="entity-item" data-test={`entity-item-${el.idCbiEntity}`}>
        <div className="flex flex-row justify-between">
          <div>
            <span data-test="entity-name" className="entity-name font-bold">
              {el.name} ({el.idCbiEntity})
            </span>
            <div className="entity-link">
              <a
                data-test="entity-url"
                className="text-blue-500"
                target="_blank"
                href={`//${el.url}`}
                rel="noreferrer"
              >
                {el.url}
              </a>
            </div>
          </div>
        </div>
        {(!!el?.alias || !!el?.altUrl) && (
          <Space>
            {el?.alias && (
              <>
                <span className="font-semibold">Alias: </span>
                <span>{el?.alias}</span>
              </>
            )}
            {el?.altUrl && (
              <>
                <span className="font-semibold">Alternate URL: </span>
                <a
                  data-test="entity-alt-url"
                  className="text-blue-500"
                  target="_blank"
                  href={`//${el?.altUrl}`}
                  rel="noreferrer"
                >
                  {el?.altUrl}
                </a>
              </>
            )}
          </Space>
        )}
      </div>
    ),
  }));

  const options: DefaultOptionType[] = [
    {
      value: entity?.isFetching ? 'loading' : undefined,
      label: <Spin size="small" />,
      disabled: true,
    } as any,
  ]
    .filter((el) => !!el?.value)
    .concat(entities);

  return (
    <Form.Item name={name} label={label}>
      <Select
        data-test="entity-search"
        suffixIcon={<SearchOutlined className="absolute search-icon" />}
        onSearch={debounceFetcher}
        showArrow={false}
        placeholder={placeholder}
        loading={entity?.isFetching}
        mode="multiple"
        filterOption={false}
        showSearch
        notFoundContent={entity?.isFetching ? <Spin size="small" /> : undefined}
        options={options}
        labelInValue
        className='w-full'
      />
    </Form.Item>
  );
};
